// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useState } from 'react';

import type { ResolvedTheme, ThemePreference } from '../theme';
import {
  applyTheme,
  readStoredPreference,
  resolveTheme,
  storePreference,
} from '../theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Whether the OS currently asks for dark. Falls back to light (`false`) when
 * it cannot be asked, matching the FOUC guard's light fallback in
 * `index.html` — light is the app default, not dark.
 */
function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_QUERY).matches
    : false;
}

export interface UseThemeResult {
  /** The user's choice, including `system`. */
  preference: ThemePreference;
  /** What is actually painted right now — `light` or `dark`. */
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

/**
 * Own the theme: read the stored preference (light by default — see
 * `DEFAULT_THEME_PREFERENCE` in `theme.ts`), follow the OS live while it is
 * `system`, paint the resolved value onto `<html data-theme>`, and persist any
 * change. The inline script in `index.html` has already applied the correct
 * theme before first paint; this hook keeps it in sync afterwards, so mounting
 * it re-applies the identical value (idempotent) rather than causing a flash.
 *
 * Mount exactly one instance: `AppShell` calls it and passes the result to
 * both `ThemeToggle`s (title bar and drawer). A second instance would add a
 * redundant `matchMedia` listener and keep its own `preference`, so two
 * toggles would drift apart.
 */
export function useTheme(): UseThemeResult {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    readStoredPreference(),
  );
  const [prefersDark, setPrefersDark] = useState<boolean>(() => systemPrefersDark());

  // Track the OS setting so a `system` preference follows it without a reload.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const resolved = resolveTheme(preference, prefersDark);

  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    storePreference(next);
  }, []);

  return { preference, resolved, setPreference };
}
